"use client";

/**
 * ErpShell — full app shell wrapper za sve ERP stranice (1:1 mockup port).
 *
 * Mockup: docs/mockups/erp-skeleton-v1-popunjen.html (1710 lin, LOCKED).
 * Layout:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │  ErpTopbar (56px)                                    │
 *   ├────────────┬─────────────────────────────────────────┤
 *   │            │                                         │
 *   │ ErpSidebar │  <main>{children}</main>                │
 *   │  (240px)   │                                         │
 *   │            │                                         │
 *   └────────────┴─────────────────────────────────────────┘
 *
 * Drži:
 *  - RequireAuth gate (redirect na auth.grozd.me ako nije autentikovan)
 *  - AuthExpiryRedirect — replace-redirect na 401 lifecycle
 *  - ErpTopbar (56px) — brand + mega meni + sveta zona desno
 *  - ErpSidebar (240px) — nav po sekcijama, active link
 *  - <main> sa scroll-able content area
 *
 * Pravila:
 *  - PRAVILO 11: meniji KLIK, ne hover
 *  - PRAVILO 12: CSS vars only, dark mode, Manrope/Inter, crnogorski latinica
 *  - PRAVILO 13: SVETA ZONA desno se nikad ne pomjera
 */

import * as React from "react";
import { RequireAuth, fetchMe } from "@grozd/auth-client";
import type { AuthUser } from "@grozd/auth-client";
import { ErpTopbar } from "./ErpTopbar";
import { ErpSidebar } from "./ErpSidebar";
import { AuthExpiryRedirect } from "./AuthExpiryRedirect";
import { installFetchInterceptor } from "@/lib/auth/access-denied-redirect";

interface ErpShellProps {
  children: React.ReactNode;
}

function ErpShellInner({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(null);

  // Globalni 403 lifecycle redirect handler — opt-in jednom za cijeli app.
  // Idempotentan (interno guard sa window.__grozd_fetch_lifecycle_intercept__).
  React.useEffect(() => {
    installFetchInterceptor();
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetchMe().then((u) => {
      if (!cancelled) setUser(u);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Init theme from localStorage (sinhronizacija sa data-theme atributom)
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem("grozd-theme");
      if (stored === "dark" || stored === "light") {
        document.documentElement.setAttribute("data-theme", stored);
      }
    } catch {
      // ignore (SSR ili odbijen storage)
    }
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--surface-base)", color: "var(--ink)" }}
    >
      <ErpTopbar user={user} />
      <div className="flex flex-1 min-h-0" style={{ minHeight: 0 }}>
        <ErpSidebar />
        <main
          className="flex-1 min-w-0"
          id="main"
          style={{
            background: "var(--surface-base)",
            overflowY: "auto",
            height: "calc(100vh - 56px)",
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

export function ErpShell({ children }: ErpShellProps) {
  return (
    <RequireAuth>
      {/* AuthExpiryRedirect — na 401 (session expiry) replace na auth login.
          Vidi docs/za-nas/2026-05-04-session-expiry-flood-analysis.md (Sloj 2). */}
      <AuthExpiryRedirect />
      <ErpShellInner>{children}</ErpShellInner>
    </RequireAuth>
  );
}

export default ErpShell;
